"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _Animation = _interopRequireDefault(require("./Animation"));

var _utils = require("../../utils");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var __extends = void 0 && (void 0).__extends || function () {
  var extendStatics = function (d, b) {
    extendStatics = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p];
    };

    return extendStatics(d, b);
  };

  return function (d, b) {
    extendStatics(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

/**
 * A `TileAnimation` object is a subclass of `Animation` that is responsible for loading and
 * managing a series of tile layers required for animation playback.
 */
var TileAnimation =
/** @class */
function (_super) {
  __extends(TileAnimation, _super);
  /**
   * Initializes a new animation instance with the provided configuration.
   *
   * @param {TileAnimationOptions} [opts=null]
   * @memberof TileAnimation
   */


  function TileAnimation(opts) {
    if (opts === void 0) {
      opts = null;
    }

    var _this = this;

    opts = (0, _utils.extend)({}, {
      refresh: 0,
      showWhenLoading: true,
      showLoadingIntervals: false,
      bufferIntervals: -1,
      reverseLoadOrder: false,
      times: undefined
    }, opts);
    _this = _super.call(this, opts) || this;
    _this._layersByTime = null;
    _this._isReset = false;
    _this._loading = false;
    _this._hasLoaded = false;
    _this._visible = true;

    if ((0, _utils.isset)(_this.opts.times)) {
      _this.setTimes(_this.opts.times);
    }

    _this._startup();

    return _this;
  }

  Object.defineProperty(TileAnimation.prototype, "showWhenLoading", {
    get: function () {
      return this.opts.showWhenLoading;
    },
    set: function (value) {
      this.opts.showWhenLoading = value;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(TileAnimation.prototype, "bufferCount", {
    get: function () {
      var totalBufferIntervals = this.opts.bufferIntervals;

      if (totalBufferIntervals === -1) {
        return this.totalIntervals;
      } // if animation is future and animation contains future and past, then we need to reduce the buffer intervals
      // for this animation by those required by the associated past animation


      if (this.isFuture() && this.containsPast()) {
        totalBufferIntervals = this.opts.bufferIntervals - (this.opts.intervals - this.totalIntervals);
      } // buffer count should never be greater than the times required for the animation


      return Math.max(0, Math.min(totalBufferIntervals, this.totalIntervals));
    },
    enumerable: false,
    configurable: true
  });
  /**
   * Returns the animation data keyed by time interval.
   *
   * @returns {{ [key: number]: any }}
   * @memberof TileAnimation
   */

  TileAnimation.prototype.dataByTime = function () {
    return this._layersByTime || {};
  };
  /**
   * Returns an array of cached raster data items for the animation.
   *
   * @returns {any[]}
   * @memberof TileAnimation
   */


  TileAnimation.prototype.dataItems = function () {
    var _this = this;

    return Object.keys(this.dataByTime()).map(function (k) {
      return _this._layersByTime[k];
    });
  };
  /**
   * Begins playing the animation if not currently running.
   *
   * If images required for playback have not been loaded, then all images will be requested
   * before playback can begin.
   *
   * @returns
   * @memberof TileAnimation
   */


  TileAnimation.prototype.play = function () {
    if (this.isAnimating()) return; // // if from and to are the same times, just go to that time and don't animate
    // if (!this.canAnimate()) {
    // 	return;
    // }

    var totalIntervals = this.totalIntervals;
    var needed = this.bufferCount;

    if ((needed > 0 || this.bufferCount === 0) && (!this._hasImages() && totalIntervals > 0 || this._totalImages() < needed)) {
      this._load();

      return;
    }

    this.show();

    _super.prototype.play.call(this);
  };
  /**
   * Stops playing the animation if currently running.
   *
   * Starting the animation again using `start()` after calling `stop()` will restart the
   * animation from the beginning.
   *
   * @memberof TileAnimation
   */


  TileAnimation.prototype.stop = function () {
    _super.prototype.stop.call(this);

    if (this._loading) {
      this.reset();
    }
  };
  /**
   * Resets the animation to its original state.
   *
   * All images that were cached during animation playback will be removed and new images will
   * have to be requested again when playback begins.
   *
   * @memberof TileAnimation
   */


  TileAnimation.prototype.reset = function (updateTime) {
    var _this = this;

    if (updateTime === void 0) {
      updateTime = false;
    }

    _super.prototype.reset.call(this, updateTime);

    this.hide();

    if (this._reloadTimer) {
      window.clearTimeout(this._reloadTimer);
    }

    this._isReset = true;
    this._loading = false;
    this._hasLoaded = false;
    this._layersByTime = null;
    this._currentLayer = null; // trigger removal of all layers associated with the animation

    if (this._layersByTime) {
      Object.keys(this._layersByTime).forEach(function (key) {
        var time = parseInt(key, 10);

        _this._layersByTime[time].remove();

        delete _this._layersByTime[time];
      });
    }
  };
  /**
   * Returns whether the animation is ready for playback.
   *
   * A tile animation is only ready once all images have been loaded.
   *
   * @returns {boolean}
   * @memberof TileAnimation
   */


  TileAnimation.prototype.isReady = function () {
    var needed = this.bufferCount;

    var loaded = this._totalImages();

    return this.canBeginPlayback() && (this._hasImages() && loaded >= needed || needed === 0);
  };

  TileAnimation.prototype.needsData = function () {
    return this._totalImages() < this.totalIntervals;
  };

  TileAnimation.prototype.canBeginPlayback = function () {
    if (this.bufferCount >= 0) {
      return this._totalImages() >= this.bufferCount;
    }

    return !this.isLoading();
  };
  /**
   * Returns whether the animation can animate based on whether or not animation data has
   * loaded or is already cached.
   *
   * @returns {boolean}
   * @memberof TileAnimation
   */


  TileAnimation.prototype.canAnimate = function () {
    var can = _super.prototype.canAnimate.call(this);

    if (can && this._hasLoaded && this.needsData()) {
      can = false; // } else if (can && ((!this.isFuture() && !this.containsPast())) || (this.isFuture() && !this.containsFuture())) {
      // 	can = false;
    }

    return can;
  };
  /**
   * Returns whether the animation is currently loading data.
   *
   * @returns {boolean}
   * @memberof TileAnimation
   */


  TileAnimation.prototype.isLoading = function () {
    return this._loading;
  };
  /**
   * Shows the animation content.
   *
   * @memberof TileAnimation
   */


  TileAnimation.prototype.show = function () {
    this._visible = true;

    if (this._currentLayer) {
      this._currentLayer.show();
    }
  };
  /**
   * Hides the animation content.
   *
   * @memberof TileAnimation
   */


  TileAnimation.prototype.hide = function () {
    this._visible = false;

    if (this._currentLayer) {
      this._currentLayer.hide();
    }
  };
  /**
   * Returns whether the animation element is currently visible.
   *
   * @returns {boolean}
   * @memberof TileAnimation
   */


  TileAnimation.prototype.isVisible = function () {
    if (this._currentLayer) {
      return this._currentLayer.isVisible();
    }

    return true;
  };
  /**
   * Updates the opacity for all raster-related data associated with the animation.
   *
   * @param {number} value
   * @memberof ImageAnimation
   */


  TileAnimation.prototype.setOpacity = function (value) {
    this.dataItems().forEach(function (item) {
      if ((0, _utils.has)(item, 'setOpacity')) {
        item.setOpacity(value, true);
      }
    });
  };
  /**
   * @ignore
   * Updates the animation by showing the appropriate tile layer for the specifyed time.
   *
   * @protected
   * @param {number} time
   * @memberof TileAnimation
   */


  TileAnimation.prototype._updateForTime = function (time) {
    if (this.canShow(this.currentTime)) {
      this.show();
    } else {
      this.hide();
    } // if (this.isAnimating() === false) return;


    if (this._visible === false) return;

    var layer = this._frameClosestToTime(time);

    if (layer && layer !== this._currentLayer) {
      layer.show();

      if (this._currentLayer) {
        this._currentLayer.hide();
      }

      this._currentLayer = layer;
    } // if (this.canShow(this.currentTime)) {
    // 	this.show();
    // } else {
    // 	this.hide();
    // }

  };
  /**
   * @ignore
   * Returns the tile layer closest to the specified time.
   *
   * @private
   * @param {number} time
   * @returns {ITileLayer}
   * @memberof TileAnimation
   */


  TileAnimation.prototype._frameClosestToTime = function (time) {
    if (Object.keys(this.dataByTime()).length === 0) {
      return null;
    }

    var times = Object.keys(this._layersByTime).sort().reverse().map(function (o) {
      return parseInt(o, 10);
    }); // eslint-disable-line max-len

    var image = null;
    var closest = times[times.length - 1];
    var diff = Math.abs(time - closest);
    times.forEach(function (t) {
      var checkDiff = Math.abs(time - t);

      if (checkDiff < diff) {
        diff = checkDiff;
        closest = t;
      }
    });

    if (closest !== this._lastImageTime && this._layersByTime[closest]) {
      image = this._layersByTime[closest];
      closest = Math.round(closest);
      this._lastImageTime = closest;
      this.trigger(_Animation.default.Event.ADVANCE_IMAGE, {
        time: closest,
        layer: image
      });
    }

    return image;
  };
  /**
   * @ignore
   * Returns whether animation layers are available.
   *
   * @private
   * @returns {boolean}
   * @memberof TileAnimation
   */


  TileAnimation.prototype._hasImages = function () {
    return this._totalImages() > 0;
  };
  /**
   * @ignore
   * Returns the total number of animation layers.
   *
   * @private
   * @returns {number}
   * @memberof TileAnimation
   */


  TileAnimation.prototype._totalImages = function () {
    return Object.keys(this.dataByTime()).length;
  };
  /**
   * @ignore
   * Loads all tile layers based on the required time intervals.
   *
   * @private
   * @returns
   * @memberof TileAnimation
   */


  TileAnimation.prototype._load = function () {
    var _this = this;

    if (this._loading) {
      return;
    }

    var times = this.neededIntervals();

    if (!times || times.length === 0) {
      this._hasLoaded = true;
      this.trigger(_Animation.default.Event.LOAD_DONE);
      this.trigger(_Animation.default.Event.READY);
      return;
    }

    this._isReset = false;
    this._loading = true;
    var bufferCount = this.bufferCount;

    if (this._layersByTime) {
      // reduce times by interval images we may already have cached
      Object.keys(this._layersByTime).forEach(function (key) {
        var time = parseInt(key, 10);
        var index = times.indexOf(time);

        if (index !== -1) {
          times.splice(index, 1);
        }
      });
    }

    var onLoadComplete = function () {
      _this._loading = false;
      _this._hasLoaded = true;

      _this.trigger(_Animation.default.Event.LOAD_DONE);

      if (bufferCount === -1 || bufferCount >= times.length) {
        _this.trigger(_Animation.default.Event.READY);
      }

      if (!_this.timeline) {
        _this.play();
      }
    };

    if (times.length === 0) {
      onLoadComplete();
      return;
    } // if bufferCount is less than total times, then reverse times array so that
    // the most recent intervals are loaded and rendered first


    if (bufferCount >= 0 && this.opts.reverseLoadOrder === true) {
      times = times.reverse();
    }

    this.trigger(_Animation.default.Event.LOAD_START, {
      times: times
    });

    var loadTimes = function (timesToLoad) {
      var wait = _this.opts.showLoadingIntervals === false && bufferCount !== 0;
      timesToLoad.forEach(function (time) {
        _this._loadInterval(time, true, wait, function () {
          if (_this._totalImages() === times.length) {
            onLoadComplete();
          }
        });
      });
    };

    var loadingInterval = 0;

    var loadNextInterval = function () {
      var wait = _this.opts.showLoadingIntervals === false || bufferCount !== 0 && loadingInterval <= _this._totalImages();

      _this._loadInterval(times[loadingInterval], true, wait, function () {
        loadingInterval += 1; // don't continue loading remaining intervals if animation was reset during load process

        if (!_this._isReset) {
          if (loadingInterval >= times.length) {
            onLoadComplete(); // start refresh timer if needed

            if (_this.opts.refresh > 0) {
              if (_this._reloadTimer) {
                window.clearTimeout(_this._reloadTimer);
              }

              _this._reloadTimer = setTimeout(function () {
                _this.reset();

                _this._load();
              }, _this.opts.refresh * 1000);
            }
          } else if (bufferCount > 0 && _this._totalImages() === bufferCount) {
            _this.trigger(_Animation.default.Event.READY);

            if (_this.opts.showLoadingIntervals) {
              loadTimes(times.slice(loadingInterval));
            } else {
              loadNextInterval();
            }
          } else {
            loadNextInterval();
          }
        }
      });
    };

    if (bufferCount === 0) {
      this.trigger(_Animation.default.Event.READY);
      loadTimes(times);
    } else {
      loadNextInterval();
    }
  };
  /**
   * @ignore
   * Loads a single tile layer for the specified time.
   *
   * @private
   * @param {number} time
   * @param {boolean} cache
   * @param {() => void} callback
   * @returns
   * @memberof TileAnimation
   */


  TileAnimation.prototype._loadInterval = function (time, cache, wait, callback) {
    var _this = this;

    if (callback === void 0) {
      callback = null;
    }

    time = Math.round(time);

    if (!this._layersByTime) {
      this._layersByTime = {};
    } else if (this._layersByTime[time]) {
      // skip the request if we already have a cached image for this interval
      if (callback) {
        callback();
      }

      return;
    }

    var date = new Date(time);

    if (this.provider) {
      this.provider.animationLayerForDate(this, date, wait).then(function (layer) {
        // if animation was reset while loading the interval, then immediately remove it
        if (_this._isReset) {
          layer.remove();
          return;
        }

        if (_this.showWhenLoading && _this._totalImages() < _this.bufferCount) {
          if (_this._currentLayer) {
            _this._currentLayer.hide();
          }

          layer.show();
          _this._currentLayer = layer;
        }

        _this._layersByTime[time] = layer;

        _this.trigger(_Animation.default.Event.LOAD_IMAGE, {
          date: new Date(time),
          layer: layer
        });

        _this.trigger(_Animation.default.Event.LOAD_PROGRESS, {
          time: time,
          loaded: Object.keys(_this._layersByTime).length,
          total: _this.totalIntervals
        });

        if (callback) {
          callback();
        }
      }).catch(function (e) {
        console.error('Failed to load tile animation interval', e); // eslint-disable-line no-console
      });
    }
  };

  return TileAnimation;
}(_Animation.default);

var _default = TileAnimation;
exports.default = _default;
module.exports = exports.default;